import type { MessageKey } from '@dijitalapp/i18n';
import type { ConversationDto, LearnOverviewDto } from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat } from '../lib/chat-context';
import { useShell } from '../pages/Shell';
import { Icon } from './Icon';
import { SectionNav } from './SectionNav';

/**
 * The middle pane follows the rail, like a chat app's sections: each area lists its own things here
 * (spaces, calendar views, classes, channels) instead of the chat list.
 */

const initial = (name: string) => Array.from(name.trim())[0]?.toUpperCase() ?? '?';

function NewButton({ to, label }: { to: string; label: string }) {
  return (
    <NavLink to={to} className="icon-btn" title={label} aria-label={label}>
      <Icon name="plus" />
    </NavLink>
  );
}

export function SpacesNav() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { spaces, capabilities } = useShell();
  const canCreate = (capabilities?.createSpaceTypes.length ?? 0) > 0;
  return (
    <SectionNav
      title={t('spaces.title')}
      base="/spaces"
      action={canCreate ? <NewButton to="/spaces/new" label={t('spaces.create')} /> : undefined}
      items={spaces.map((s) => ({
        to: `/spaces/${s.id}`,
        label: s.name,
        avatar: initial(s.name),
        description: `${t(`spaces.type.${s.type}` as MessageKey)} · ${t('spaces.members', { count: s.memberCount })}`,
      }))}
    />
  );
}

export function PlannerNav() {
  const { i18n, config } = useApp();
  const { t } = i18n;
  return (
    <SectionNav
      title={t('planner.title')}
      base="/calendar"
      items={[
        ...(config?.flags.calendar ? [{ to: '/calendar/events', label: t('planner.calendar'), icon: 'calendar' as const, description: t('planner.calendarDesc') }] : []),
        ...(config?.flags.tasks ? [{ to: '/calendar/tasks', label: t('planner.tasks'), icon: 'list' as const, description: t('planner.tasksDesc') }] : []),
      ]}
    />
  );
}

export function LearnNav() {
  const { i18n, me } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const [data, setData] = useState<LearnOverviewDto | null>(null);
  useEffect(() => {
    let alive = true;
    api<LearnOverviewDto>('GET', '/learn').then(
      (d) => alive && setData(d),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [chat.learnVersion]);
  const classes = data?.classes ?? [];
  const isStudent = classes.some((c) => c.myRole === 'student');
  return (
    <SectionNav
      title={t('learn.title')}
      base="/learn"
      groups={[
        {
          items: [
            { to: '/learn/home', label: t('learn.nav.home'), icon: 'learn', description: t('learn.nav.homeDesc') },
            ...(isStudent || classes.length === 0 ? [{ to: '/learn/plan', label: t('study.title'), icon: 'calendar' as const, description: t('learn.nav.planDesc') }] : []),
          ],
        },
        ...(classes.length
          ? [
              {
                title: t('learn.classes'),
                items: classes.map((c) => ({
                  to: `/learn/class/${c.id}`,
                  label: c.name,
                  avatar: initial(c.name),
                  description: `${c.schoolName} · ${t(c.myRole === 'teacher' ? 'learn.role.teacher' : 'learn.role.student')}`,
                })),
              },
            ]
          : []),
        ...(me?.ageBand === 'adult' ? [{ items: [{ to: '/learn/school', label: t('learn.request.title'), icon: 'plus' as const, description: t('learn.nav.schoolDesc') }] }] : []),
      ]}
    />
  );
}

export function DiscoverNav() {
  const { i18n, me } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const mine = chat.conversations.filter((c: ConversationDto) => c.type === 'channel').length;
  return (
    <SectionNav
      title={t('channel.title')}
      base="/discover"
      items={[
        { to: '/discover/mine', label: t('channel.mine'), icon: 'messages', ...(mine ? { badge: String(mine) } : {}) },
        { to: '/discover/browse', label: t('channel.directory'), icon: 'discover' },
        ...(me?.ageBand === 'adult' ? [{ to: '/discover/new', label: t('channel.create'), icon: 'plus' as const }] : []),
      ]}
    />
  );
}
